import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import Cookies from "js-cookie";
import { IoIosRefresh } from "react-icons/io";
import Header from "./Header";
import * as opOrderStatsService from "../services/openprintStats.service";

// Client-side mirror of OpOrdersApi.py's SHIPPING_PERFORMANCE_ALLOWED_EMAILS.
// This only controls whether the page renders — the backend independently
// enforces the same list on every request, so this is not the real gate.
const ALLOWED_EMAILS = ["luis.morfin@photomugs.com", "horry.chor@photomugs.com"];

const formatNumber = (value) => (value ?? 0).toLocaleString();

// Local "today" as YYYY-MM-DD, for the date picker's default value.
const todayDateString = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

function WorkerCard({ worker }) {
  return (
    <div className="flex flex-col rounded-2xl border-2 border-red-500 bg-gray-700/40 px-3 py-4 sm:px-6 sm:py-6 shadow-md">
      <p className="text-xs sm:text-lg font-bold uppercase tracking-wider text-red-300">
        {worker.name}
      </p>
      <div className="mt-3">
        <p className="text-3xl sm:text-5xl lg:text-6xl font-extrabold leading-none text-white tabular-nums">
          {formatNumber(worker.order_count)}
        </p>
        <p className="mt-1 text-xs uppercase tracking-wide text-gray-400">
          Orders Shipped
        </p>
      </div>
      <div className="mt-4 pt-4 border-t border-white/10">
        <p className="text-xl sm:text-3xl lg:text-4xl font-bold leading-none text-white tabular-nums">
          {formatNumber(worker.order_item_qty)}
        </p>
        <p className="mt-1 text-xs uppercase tracking-wide text-gray-400">
          Items Shipped
        </p>
      </div>
    </div>
  );
}

export default function ShippingPerformancePage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(todayDateString);

  const currEmail = (localStorage.getItem("UserEmail") || "").toLowerCase();
  const cookie = Cookies.get("token");
  const notLoggedIn = !currEmail || !cookie;
  const authorized = ALLOWED_EMAILS.includes(currEmail);

  const fetchData = (date) => {
    setLoading(true);
    setError(null);
    opOrderStatsService
      .getShippingPerformance(date)
      .then((response) => {
        setData(response);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setError(
          err?.response?.status === 403
            ? "You are not authorized to view this page."
            : "Failed to load shipping performance data.",
        );
        setLoading(false);
      });
  };

  // Refetch whenever the selected date changes (including the initial mount,
  // which defaults to today via the selectedDate initializer above).
  useEffect(() => {
    if (!notLoggedIn && authorized) {
      fetchData(selectedDate);
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  if (notLoggedIn) {
    return <Navigate to="/" replace={true} />;
  }
  if (!authorized) {
    return <Navigate to="/openprint" replace={true} />;
  }

  return (
    <div className="grid grid-cols-10 gap-2">
      <div className="col-span-10">
        <Header compNum={6} system="OP" />
        <div className="mt-2 px-2 pb-8">
          <div className="bg-gray-800 rounded-lg shadow-lg p-3 sm:p-6">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <div>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-red-300">
                  Shipping Performance
                </h1>
                <p className="text-base sm:text-lg lg:text-xl text-white mt-1">
                  Orders and items shipped per worker, 12:00am–11:59pm on{" "}
                  {selectedDate}.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <label className="text-sm text-gray-300" htmlFor="shipping-performance-date">
                  Date
                </label>
                <input
                  id="shipping-performance-date"
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="px-3 py-1.5 text-sm text-white bg-gray-700 border border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={() => fetchData(selectedDate)}
                  className="text-white hover:text-green-400"
                  title="Refresh"
                >
                  <IoIosRefresh size={22} />
                </button>
              </div>
            </div>

            {error && (
              <div className="mb-4 px-4 py-3 text-sm text-red-200 bg-red-900 rounded border border-red-700">
                {error}
              </div>
            )}

            {loading ? (
              <div className="flex justify-center py-16 text-gray-400">
                Loading…
              </div>
            ) : (
              <div className="space-y-8">
                {(data?.teams || []).map((team) => (
                  <div key={team.team}>
                    <h2 className="text-lg sm:text-xl font-semibold text-white mb-3 pb-2 border-b border-gray-600">
                      {team.team}
                    </h2>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                      {team.workers.map((worker) => (
                        <WorkerCard key={worker.name} worker={worker} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
